// Offline photo queue — stores photo/file blobs in IndexedDB and uploads them
// automatically when connectivity returns. Survives app restarts and reboots.
import axios from "axios";
import { API } from "@/lib/api";

const DB_NAME = "pcc_photo_queue";
const STORE = "photos";

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

const tx = async (mode, fn) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    const out = fn(store);
    t.oncomplete = () => { db.close(); resolve(out?.result); };
    t.onerror = () => { db.close(); reject(t.error); };
  });
};

export async function queuePhoto(orderId, file) {
  const entry = {
    id: (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`),
    orderId,
    name: file.name || "photo.jpg",
    type: file.type || "application/octet-stream",
    blob: file,
    ts: Date.now(),
  };
  await tx("readwrite", (s) => s.put(entry));
  window.dispatchEvent(new CustomEvent("photo-queue-changed"));
  return entry;
}

export async function getQueuedPhotos(orderId) {
  const all = await new Promise(async (resolve, reject) => {
    const db = await openDB();
    const t = db.transaction(STORE, "readonly");
    const req = t.objectStore(STORE).getAll();
    req.onsuccess = () => { db.close(); resolve(req.result || []); };
    req.onerror = () => { db.close(); reject(req.error); };
  });
  return orderId ? all.filter((p) => p.orderId === orderId) : all;
}

async function removePhoto(id) {
  await tx("readwrite", (s) => s.delete(id));
}

let flushing = false;
export async function flushPhotos() {
  if (flushing || !navigator.onLine) return;
  const queued = await getQueuedPhotos().catch(() => []);
  if (queued.length === 0) return;
  flushing = true;
  try {
    // Group by order so files land on the right order in one update each
    const byOrder = {};
    queued.forEach((p) => { (byOrder[p.orderId] = byOrder[p.orderId] || []).push(p); });
    let anySynced = false;
    for (const [orderId, photos] of Object.entries(byOrder)) {
      const uploaded = [];
      const doneIds = [];
      for (const p of photos) {
        try {
          const fd = new FormData();
          fd.append("file", p.blob, p.name);
          const { data } = await axios.post(`${API}/orders/upload`, fd, { timeout: 60000 });
          uploaded.push({ url: data.url, name: data.file_name });
          doneIds.push(p.id);
        } catch (err) {
          if (err.response?.status === 400) doneIds.push(p.id); // permanently rejected — drop
        }
      }
      if (uploaded.length > 0) {
        try {
          const { data: orders } = await axios.get(`${API}/orders`, { timeout: 15000 });
          const order = orders.find((o) => o.id === orderId);
          if (order) {
            const existing = order.files || (order.file_url ? [{ url: order.file_url, name: order.file_name }] : []);
            await axios.put(`${API}/orders/${orderId}`, { files: [...existing, ...uploaded] }, { timeout: 15000 });
          }
          // Order gone from server (deleted) — drop the uploads silently
          for (const id of doneIds) await removePhoto(id);
          anySynced = true;
        } catch {
          // PUT failed — keep queued, retry later (uploads will re-run; server normalizes dupes are acceptable)
        }
      } else {
        for (const id of doneIds) await removePhoto(id);
      }
    }
    if (anySynced) window.dispatchEvent(new CustomEvent("photo-queue-flushed"));
    window.dispatchEvent(new CustomEvent("photo-queue-changed"));
  } finally {
    flushing = false;
  }
}

window.addEventListener("online", () => setTimeout(flushPhotos, 1500));
setInterval(flushPhotos, 15000);
setTimeout(flushPhotos, 4000);
